<template>
  <div class="payment-types-management">
      <!-- Заголовок с кнопкой добавления -->
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
        <div>
          <p style="margin: 4px 0 0 0; color: #8c8c8c; font-size: 14px;">
            Создавайте и настраивайте различные типы оплат для команды
          </p>
        </div>
      </div>

      <!-- Статистика -->
      <div style="margin-bottom: 16px; padding: 12px 16px; background: #fafafa; border-radius: 6px;">
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px;">
          <div style="text-align: center;">
            <div style="font-size: 16px; font-weight: bold; color: #262626;">{{ paymentTypes.length }}</div>
            <div style="font-size: 10px; color: #666;">Всего типов</div>
          </div>
          <div style="text-align: center;">
            <div style="font-size: 16px; font-weight: bold; color: #52c41a;">{{ activeTypesCount }}</div>
            <div style="font-size: 10px; color: #666;">Активных</div>
          </div>
          <div style="text-align: center;">
            <div style="font-size: 16px; font-weight: bold; color: #1890ff;">{{ subscriptionTypesCount }}</div>
            <div style="font-size: 10px; color: #666;">Абонементов</div>
          </div>
          <div style="text-align: center;">
            <div style="font-size: 16px; font-weight: bold; color: #faad14;">{{ oneTimeTypesCount }}</div>
            <div style="font-size: 10px; color: #666;">Разовых</div>
          </div>
        </div>
      </div>

      <!-- Таблица типов оплат (Desktop) -->
      <div class="desktop-view">
        <a-table
          :data-source="paymentTypes"
          :columns="columns"
          :loading="loading"
          :pagination="false"
          row-key="id"
          size="small"
        >
        <template #bodyCell="{ column, record }">
          <!-- Тип оплаты -->
          <template v-if="column.key === 'type'">
            <a-tag :color="getTypeColor(record.type)">
              {{ getTypeText(record.type) }}
            </a-tag>
          </template>

          <!-- Категория -->
          <template v-if="column.key === 'category'">
            <a-tag :color="getCategoryColor(record.category)">
              {{ getCategoryText(record.category) }}
            </a-tag>
          </template>

          <!-- Частота -->
          <template v-if="column.key === 'frequency'">
            <span v-if="record.type === 'subscription'">
              {{ getFrequencyText(record.frequency) }}
            </span>
            <span v-else style="color: #8c8c8c;">-</span>
          </template>

          <!-- Статус -->
          <template v-if="column.key === 'status'">
            <a-tag :color="record.is_active ? 'green' : 'red'">
              {{ record.is_active ? 'Активен' : 'Неактивен' }}
            </a-tag>
          </template>

          <!-- Блокировка участия -->
          <template v-if="column.key === 'blocking'">
            <a-tag v-if="record.can_block_participation" color="orange">
              Блокирует
            </a-tag>
            <span v-else style="color: #8c8c8c;">-</span>
          </template>

          <!-- Действия -->
          <template v-if="column.key === 'actions'">
            <a-space>
              <a-button
                size="small"
                type="primary"
                @click="editPaymentType(record)"
                title="Редактировать"
              >
                <template #icon>
                  <EditOutlined />
                </template>
              </a-button>
              <a-button
                size="small"
                type="default"
                @click="manageAdmins(record)"
                title="Управление администраторами"
              >
                <template #icon>
                  <UserOutlined />
                </template>
              </a-button>
              <a-button
                size="small"
                :type="record.is_active ? 'default' : 'primary'"
                @click="togglePaymentTypeStatus(record)"
                :title="record.is_active ? 'Деактивировать' : 'Активировать'"
              >
                <template #icon>
                  <PoweroffOutlined />
                </template>
              </a-button>
              <a-button
                size="small"
                type="text"
                danger
                @click="deletePaymentType(record)"
                :disabled="!canDeletePaymentType(record)"
                :title="canDeletePaymentType(record) ? 'Удалить' : 'Нельзя удалить: есть существующие платежи или освобождения'"
              >
                <template #icon>
                  <DeleteOutlined />
                </template>
              </a-button>
            </a-space>
          </template>
        </template>
      </a-table>
      </div>

      <!-- Мобильный вид (карточки) -->
      <div class="mobile-view">
        <div v-if="loading" class="loading-container">
          <a-spin size="large" />
          <p>Загрузка типов оплат...</p>
        </div>
        
        <div v-else-if="paymentTypes.length === 0" class="no-data">
          <a-empty description="Нет типов оплат" />
        </div>
        
        <div v-else class="payment-cards">
          <div 
            v-for="paymentType in paymentTypes" 
            :key="paymentType.id" 
            class="payment-card"
          >
            <div class="card-header">
              <div class="card-title">
                <h3>{{ paymentType.name }}</h3>
                <a-tag :color="getTypeColor(paymentType.type)" size="small">
                  {{ getTypeText(paymentType.type) }}
                </a-tag>
              </div>
              <div class="card-status">
                <a-switch 
                  v-model:checked="paymentType.is_active" 
                  @change="togglePaymentTypeStatus(paymentType)"
                  size="small"
                />
              </div>
            </div>
            
            <div class="card-content">
              <div class="card-row">
                <span class="label">Сумма:</span>
                <span class="value">{{ paymentType.amount }} {{ paymentType.currency }}</span>
              </div>
              
              <div v-if="paymentType.category" class="card-row">
                <span class="label">Категория:</span>
                <span class="value">{{ getCategoryText(paymentType.category) }}</span>
              </div>
              
              <div v-if="paymentType.frequency" class="card-row">
                <span class="label">Частота:</span>
                <span class="value">{{ getFrequencyText(paymentType.frequency) }}</span>
              </div>
              
              <div v-if="paymentType.description" class="card-description">
                {{ paymentType.description }}
              </div>
            </div>
            
            <div class="card-actions">
              <a-button 
                size="small" 
                type="text" 
                @click="editPaymentType(paymentType)"
                class="action-button"
              >
                <EditOutlined />
                Редактировать
              </a-button>
              <a-button 
                size="small" 
                type="text" 
                @click="manageAdmins(paymentType)"
                class="action-button"
              >
                <UserOutlined />
                Админы
              </a-button>
              <a-button 
                size="small" 
                type="text" 
                danger 
                @click="deletePaymentType(paymentType)"
                :disabled="!canDeletePaymentType(paymentType)"
                class="action-button"
              >
                <DeleteOutlined />
                Удалить
              </a-button>
            </div>
          </div>
        </div>
      </div>

    <!-- Модальное окно создания/редактирования -->
    <a-modal
      v-model:open="modalVisible"
      :title="isEditing ? 'Редактировать тип оплаты' : 'Создать тип оплаты'"
      :footer="null"
      width="600px"
    >
      <a-form
        ref="formRef"
        :model="form"
        :rules="rules"
        layout="vertical"
        @finish="handleSubmit"
      >
        <!-- Название -->
        <a-form-item label="Название" name="name">
          <a-input
            v-model:value="form.name"
            placeholder="Например: Абонемент на месяц"
            :maxlength="100"
          />
        </a-form-item>

        <!-- Описание -->
        <a-form-item label="Описание" name="description">
          <a-textarea
            v-model:value="form.description"
            placeholder="Подробное описание типа оплаты"
            :rows="3"
            :maxlength="500"
          />
        </a-form-item>

        <!-- Тип и категория -->
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="Тип оплаты" name="type">
              <a-select
                v-model:value="form.type"
                placeholder="Выберите тип"
                @change="handleTypeChange"
              >
                <a-select-option value="one_time">Разовая</a-select-option>
                <a-select-option value="subscription">Периодическая</a-select-option>
                <a-select-option value="special">Специальная</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Категория" name="category">
              <a-select
                v-model:value="form.category"
                placeholder="Выберите категорию"
              >
                <a-select-option value="membership">Членские взносы</a-select-option>
                <a-select-option value="equipment">Экипировка</a-select-option>
                <a-select-option value="tournament">Турниры</a-select-option>
                <a-select-option value="training">Тренировки</a-select-option>
                <a-select-option value="penalty">Штрафы</a-select-option>
                <a-select-option value="other">Прочее</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <!-- Сумма и валюта -->
        <a-row :gutter="16">
          <a-col :span="16">
            <a-form-item label="Сумма" name="amount">
              <a-input-number
                v-model:value="form.amount"
                placeholder="0.00"
                :min="0"
                :precision="2"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
          <a-col :span="8">
            <a-form-item label="Валюта" name="currency">
              <a-select v-model:value="form.currency">
                <a-select-option value="RUB">RUB</a-select-option>
                <a-select-option value="USD">USD</a-select-option>
                <a-select-option value="EUR">EUR</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <!-- Частота (только для периодических) -->
        <a-form-item
          v-if="form.type === 'subscription'"
          label="Частота"
          name="frequency"
        >
          <a-select
            v-model:value="form.frequency"
            placeholder="Выберите частоту"
            @change="handleFrequencyChange"
          >
            <a-select-option value="monthly">Ежемесячно</a-select-option>
            <a-select-option value="quarterly">Ежеквартально</a-select-option>
            <a-select-option value="yearly">Ежегодно</a-select-option>
            <a-select-option value="custom">Пользовательская</a-select-option>
          </a-select>
        </a-form-item>

        <!-- Пользовательская частота -->
        <a-form-item
          v-if="form.type === 'subscription' && form.frequency === 'custom'"
          label="Количество дней"
          name="custom_frequency_days"
        >
          <a-input-number
            v-model:value="form.custom_frequency_days"
            placeholder="Например: 45"
            :min="1"
            :max="365"
            style="width: 100%"
          />
        </a-form-item>

        <!-- Дополнительные настройки -->
        <a-divider>Дополнительные настройки</a-divider>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="Требует одобрения" name="requires_approval">
              <a-switch v-model:checked="form.requires_approval" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Блокирует участие" name="can_block_participation">
              <a-switch
                v-model:checked="form.can_block_participation"
                :disabled="form.type !== 'subscription'"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <!-- День дедлайна (только для блокирующих абонементов) -->
        <a-form-item
          v-if="form.type === 'subscription' && form.can_block_participation"
          label="День дедлайна оплаты"
          name="payment_deadline_day"
        >
          <a-input-number
            v-model:value="form.payment_deadline_day"
            placeholder="15"
            :min="1"
            :max="31"
            style="width: 100%"
          />
          <div style="font-size: 12px; color: #8c8c8c; margin-top: 4px;">
            День месяца, до которого должна быть произведена оплата
          </div>
        </a-form-item>

        <!-- Кнопки -->
        <div style="text-align: right; margin-top: 24px;">
          <a-space>
            <a-button @click="closeModal">Отмена</a-button>
            <a-button type="primary" html-type="submit" :loading="submitting">
              {{ isEditing ? 'Сохранить' : 'Создать' }}
            </a-button>
          </a-space>
        </div>
      </a-form>
    </a-modal>

    <!-- Модальное окно управления администраторами -->
    <a-modal
      v-model:open="adminsModalVisible"
      :title="`Управление администраторами: ${selectedPaymentType?.name || ''}`"
      :footer="null"
      width="800px"
    >
      <PaymentAdminsManagement
        v-if="selectedPaymentType"
        :team-id="teamId"
        :payment-type-id="selectedPaymentType.id"
        :payment-type-name="selectedPaymentType.name"
        @admin-added="onAdminAdded"
        @admin-removed="onAdminRemoved"
      />
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { 
  message, 
  Modal, 
  Card, 
  Button, 
  Table, 
  Tag, 
  Space, 
  Form, 
  FormItem, 
  Input, 
  Textarea, 
  Select, 
  SelectOption, 
  InputNumber, 
  Switch, 
  Divider, 
  Row, 
  Col 
} from 'ant-design-vue';
import {
  PlusOutlined,
  EditOutlined,
  PoweroffOutlined,
  DeleteOutlined,
  UserOutlined
} from '@ant-design/icons-vue';
import { api } from '../services/api';
import PaymentAdminsManagement from './PaymentAdminsManagement.vue';

export default {
  name: 'PaymentTypesManagement',
  components: {
    PlusOutlined,
    EditOutlined,
    PoweroffOutlined,
    DeleteOutlined,
    UserOutlined,
    PaymentAdminsManagement,
    'a-card': Card,
    'a-button': Button,
    'a-table': Table,
    'a-tag': Tag,
    'a-space': Space,
    'a-form': Form,
    'a-form-item': FormItem,
    'a-input': Input,
    'a-textarea': Textarea,
    'a-select': Select,
    'a-select-option': SelectOption,
    'a-input-number': InputNumber,
    'a-switch': Switch,
    'a-divider': Divider,
    'a-row': Row,
    'a-col': Col
  },
  props: {
    teamId: {
      type: Number,
      required: true
    }
  },
  setup(props) {
    const loading = ref(false);
    const submitting = ref(false);
    const modalVisible = ref(false);
    const isEditing = ref(false);
    const adminsModalVisible = ref(false);
    const selectedPaymentType = ref(null);
    const formRef = ref();
    const paymentTypes = ref([]);

    const form = reactive({
      name: '',
      description: '',
      type: 'subscription',
      category: 'membership',
      amount: null,
      currency: 'RUB',
      frequency: 'monthly',
      custom_frequency_days: null,
      requires_approval: true,
      can_block_participation: false,
      payment_deadline_day: 15
    });

    const rules = {
      name: [
        { required: true, message: 'Введите название типа оплаты' },
        { min: 3, max: 100, message: 'Название должно быть от 3 до 100 символов' }
      ],
      description: [
        { max: 500, message: 'Описание не должно превышать 500 символов' }
      ],
      type: [
        { required: true, message: 'Выберите тип оплаты' }
      ],
      category: [
        { required: true, message: 'Выберите категорию' }
      ],
      amount: [
        { required: true, message: 'Введите сумму' },
        { type: 'number', min: 0, message: 'Сумма должна быть положительной' }
      ],
      frequency: [
        { 
          required: true, 
          message: 'Выберите частоту',
          validator: (rule, value) => {
            if (form.type === 'subscription' && !value) {
              return Promise.reject('Выберите частоту для периодической оплаты');
            }
            return Promise.resolve();
          }
        }
      ],
      custom_frequency_days: [
        {
          validator: (rule, value) => {
            if (form.type === 'subscription' && form.frequency === 'custom' && !value) {
              return Promise.reject('Укажите количество дней для пользовательской частоты');
            }
            return Promise.resolve();
          }
        }
      ],
      payment_deadline_day: [
        {
          validator: (rule, value) => {
            if (form.type === 'subscription' && form.can_block_participation && !value) {
              return Promise.reject('Укажите день дедлайна для блокирующего абонемента');
            }
            return Promise.resolve();
          }
        }
      ]
    };

    // Вычисляемые свойства
    const activeTypesCount = computed(() => 
      paymentTypes.value.filter(pt => pt.is_active).length
    );

    const subscriptionTypesCount = computed(() => 
      paymentTypes.value.filter(pt => pt.type === 'subscription').length
    );

    const oneTimeTypesCount = computed(() => 
      paymentTypes.value.filter(pt => pt.type === 'one_time').length
    );

    // Колонки таблицы
    const columns = [
      {
        title: 'Название',
        dataIndex: 'name',
        key: 'name',
        width: '20%'
      },
      {
        title: 'Тип',
        dataIndex: 'type',
        key: 'type',
        width: '10%'
      },
      {
        title: 'Категория',
        dataIndex: 'category',
        key: 'category',
        width: '12%'
      },
      {
        title: 'Сумма',
        dataIndex: 'amount',
        key: 'amount',
        width: '10%',
        render: (amount, record) => `${amount} ${record.currency}`
      },
      {
        title: 'Частота',
        dataIndex: 'frequency',
        key: 'frequency',
        width: '10%'
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        key: 'status',
        width: '10%'
      },
      {
        title: 'Блокировка',
        dataIndex: 'blocking',
        key: 'blocking',
        width: '10%'
      },
      {
        title: 'Действия',
        key: 'actions',
        width: '18%'
      }
    ];

    // Методы
    const loadPaymentTypes = async () => {
      loading.value = true;
      try {
        const response = await api.getAdminTeamPaymentTypes(props.teamId);
        // API возвращает массив напрямую, а не объект с полем payment_types
        paymentTypes.value = Array.isArray(response) ? response : [];
        console.log('Loaded payment types:', paymentTypes.value);
      } catch (error) {
        message.error('Ошибка при загрузке типов оплат');
        console.error('Error loading payment types:', error);
      } finally {
        loading.value = false;
      }
    };

    const showCreateModal = () => {
      isEditing.value = false;
      resetForm();
      modalVisible.value = true;
    };

    const editPaymentType = (paymentType) => {
      isEditing.value = true;
      Object.assign(form, paymentType);
      modalVisible.value = true;
    };

    const closeModal = () => {
      modalVisible.value = false;
      resetForm();
    };

    const resetForm = () => {
      Object.assign(form, {
        name: '',
        description: '',
        type: 'subscription',
        category: 'membership',
        amount: null,
        currency: 'RUB',
        frequency: 'monthly',
        custom_frequency_days: null,
        requires_approval: true,
        can_block_participation: false,
        payment_deadline_day: 15
      });
      formRef.value?.resetFields();
    };

    const handleTypeChange = (value) => {
      if (value !== 'subscription') {
        form.can_block_participation = false;
        form.frequency = null;
        form.custom_frequency_days = null;
        form.payment_deadline_day = 15;
      } else {
        form.frequency = 'monthly';
      }
    };

    const handleFrequencyChange = (value) => {
      if (value !== 'custom') {
        form.custom_frequency_days = null;
      }
    };

    const handleSubmit = async () => {
      try {
        submitting.value = true;
        
        const formData = { ...form };
        
        if (isEditing.value) {
          await api.updatePaymentType(props.teamId, formData.id, formData);
          message.success('Тип оплаты обновлен');
        } else {
          await api.createPaymentType(props.teamId, formData);
          message.success('Тип оплаты создан');
        }
        
        closeModal();
        await loadPaymentTypes();
      } catch (error) {
        message.error('Ошибка при сохранении типа оплаты');
        console.error('Error saving payment type:', error);
      } finally {
        submitting.value = false;
      }
    };

    const togglePaymentTypeStatus = async (paymentType) => {
      try {
        const newStatus = !paymentType.is_active;
        await api.updatePaymentType(props.teamId, paymentType.id, {
          is_active: newStatus
        });
        
        message.success(`Тип оплаты ${newStatus ? 'активирован' : 'деактивирован'}`);
        await loadPaymentTypes();
      } catch (error) {
        console.error('Error toggling payment type status:', error);
        
        // Extract error message from API response
        let errorMessage = 'Ошибка при изменении статуса';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        message.error(errorMessage);
      }
    };

    const deletePaymentType = (paymentType) => {
      Modal.confirm({
        title: 'Удалить тип оплаты?',
        content: `Вы уверены, что хотите удалить "${paymentType.name}"? Это действие нельзя отменить. Если у этого типа оплаты есть существующие платежи или освобождения, удаление будет невозможно.`,
        okText: 'Удалить',
        okType: 'danger',
        cancelText: 'Отмена',
        onOk: async () => {
          try {
            await api.deletePaymentType(props.teamId, paymentType.id);
            message.success('Тип оплаты удален');
            await loadPaymentTypes();
          } catch (error) {
            console.error('Error deleting payment type:', error);
            
            // Extract error message from API response
            let errorMessage = 'Ошибка при удалении типа оплаты';
            if (error.response && error.response.data && error.response.data.message) {
              errorMessage = error.response.data.message;
            } else if (error.message) {
              errorMessage = error.message;
            }
            
            message.error(errorMessage);
          }
        }
      });
    };

    // Вспомогательные методы
    const getTypeColor = (type) => {
      const colors = {
        one_time: 'blue',
        subscription: 'green',
        special: 'orange'
      };
      return colors[type] || 'default';
    };

    const getTypeText = (type) => {
      const texts = {
        one_time: 'Разовая',
        subscription: 'Периодическая',
        special: 'Специальная'
      };
      return texts[type] || type;
    };

    const canDeletePaymentType = (paymentType) => {
      // Проверяем, есть ли у этого типа оплаты существующие платежи или освобождения
      // Это проверка на фронтенде для UX, реальная проверка происходит на сервере
      return true; // Показываем кнопку, но сервер проверит реальные данные
    };

    const getCategoryColor = (category) => {
      const colors = {
        membership: 'green',
        equipment: 'blue',
        tournament: 'purple',
        training: 'cyan',
        penalty: 'red',
        other: 'default'
      };
      return colors[category] || 'default';
    };

    const getCategoryText = (category) => {
      const texts = {
        membership: 'Членские взносы',
        equipment: 'Экипировка',
        tournament: 'Турниры',
        training: 'Тренировки',
        penalty: 'Штрафы',
        other: 'Прочее'
      };
      return texts[category] || category;
    };

    const getFrequencyText = (frequency) => {
      const texts = {
        monthly: 'Ежемесячно',
        quarterly: 'Ежеквартально',
        yearly: 'Ежегодно',
        custom: 'Пользовательская'
      };
      return texts[frequency] || frequency;
    };

    // Управление администраторами платежей
    const manageAdmins = (paymentType) => {
      console.log('Managing admins for payment type:', paymentType);
      selectedPaymentType.value = paymentType;
      adminsModalVisible.value = true;
    };

    const onAdminAdded = (playerId) => {
      console.log('Admin added:', playerId);
      // Можно добавить дополнительную логику при добавлении администратора
    };

    const onAdminRemoved = (adminId) => {
      console.log('Admin removed:', adminId);
      // Можно добавить дополнительную логику при удалении администратора
    };

    // Инициализация
    onMounted(() => {
      loadPaymentTypes();
    });

    // Отслеживание изменений selectedPaymentType
    watch(selectedPaymentType, (newPaymentType, oldPaymentType) => {
      if (newPaymentType && newPaymentType.id !== oldPaymentType?.id) {
        console.log('Selected payment type changed from', oldPaymentType?.id, 'to', newPaymentType.id);
        // Модальное окно уже открыто, компонент PaymentAdminsManagement должен перезагрузиться
      }
    });

    return {
      loading,
      submitting,
      modalVisible,
      isEditing,
      adminsModalVisible,
      selectedPaymentType,
      formRef,
      paymentTypes,
      form,
      rules,
      columns,
      activeTypesCount,
      subscriptionTypesCount,
      oneTimeTypesCount,
      loadPaymentTypes,
      showCreateModal,
      editPaymentType,
      closeModal,
      resetForm,
      handleTypeChange,
      handleFrequencyChange,
      handleSubmit,
      togglePaymentTypeStatus,
      deletePaymentType,
      canDeletePaymentType,
      manageAdmins,
      onAdminAdded,
      onAdminRemoved,
      getTypeColor,
      getTypeText,
      getCategoryColor,
      getCategoryText,
      getFrequencyText
    };
  }
};
</script>

<style scoped>
.payment-types-management {
  padding: 16px;
}

.ant-table {
  margin-top: 16px;
}

.ant-form-item {
  margin-bottom: 16px;
}

.ant-divider {
  margin: 24px 0 16px 0;
}

/* Responsive Design */
.desktop-view {
  display: block;
}

.mobile-view {
  display: none;
}

/* Mobile Card Styles */
.payment-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.payment-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.card-title {
  flex: 1;
}

.card-title h3 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  line-height: 1.4;
}

.card-status {
  margin-left: 12px;
}

.card-content {
  margin-bottom: 16px;
}

.card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
}

.card-row:last-child {
  margin-bottom: 0;
}

.card-row .label {
  color: #8c8c8c;
  font-weight: 500;
}

.card-row .value {
  color: #262626;
  font-weight: 600;
}

.card-description {
  margin-top: 12px;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 13px;
  color: #595959;
  line-height: 1.4;
}

.card-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  flex: 1;
  justify-content: center;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 16px;
}

.loading-container p {
  margin: 0;
  color: #8c8c8c;
  font-size: 14px;
}

.no-data {
  padding: 40px 20px;
  text-align: center;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .desktop-view {
    display: none;
  }
  
  .mobile-view {
    display: block;
  }
  
  .payment-types-management {
    padding: 12px;
  }
  
  .payment-card {
    padding: 12px;
  }
  
  .card-title h3 {
    font-size: 15px;
  }
  
  .card-row {
    font-size: 13px;
  }
  
  .card-description {
    font-size: 12px;
  }
  
  .action-button {
    font-size: 11px;
    flex: 1;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .payment-types-management {
    padding: 8px;
  }
  
  .payment-card {
    padding: 10px;
  }
  
  .card-title h3 {
    font-size: 14px;
  }
  
  .card-row {
    font-size: 12px;
  }
  
  .card-actions {
    flex-direction: row;
    gap: 6px;
  }
  
  .action-button {
    flex: 1;
    justify-content: center;
    font-size: 11px;
  }
}
</style>
